import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getQuestionVideoUrl, isWatchOnlyVideoQuestion } from '../Utils/helper';
import {
  formatVideoTime,
  getDocumentFullscreenElement,
  isIosSafariBrowser,
  isVideoInFullscreen,
  isVideoInIosNativeFullscreen,
} from '../Utils/video';

export default function useTrainingVideoGate({
  answerableTotal,
  onOnlyVideoTrainingComplete,
  questions,
  setIdentificationLocked,
  setStep,
  step,
}) {
  const isNativeIosSafariVideo = useMemo(() => isIosSafariBrowser(), []);

  const [videoGateCompleted, setVideoGateCompleted] = useState(false);
  const [videoProgressPct, setVideoProgressPct] = useState(0);
  const [videoReviewMode, setVideoReviewMode] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const [videoPlaybackRate, setVideoPlaybackRate] = useState(1);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoVolume, setVideoVolume] = useState(1);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoLoadFailed, setVideoLoadFailed] = useState(false);
  const [showFullscreenControls, setShowFullscreenControls] = useState(false);

  const videoRef = useRef(null);
  const videoShellRef = useRef(null);
  const maxWatchedTimeRef = useRef(0);
  const forceSeekingRef = useRef(false);
  const fullscreenControlsTimerRef = useRef(null);
  const fullscreenStateSyncTimersRef = useRef([]);

  const watchOnlyVideoQuestions = useMemo(
    () => (questions || []).filter(isWatchOnlyVideoQuestion),
    [questions]
  );
  const watchOnlyVideoQuestionItem = useMemo(
    () => watchOnlyVideoQuestions[currentVideoIndex] || null,
    [watchOnlyVideoQuestions, currentVideoIndex]
  );
  const watchOnlyVideoUrl = useMemo(
    () => getQuestionVideoUrl(watchOnlyVideoQuestionItem),
    [watchOnlyVideoQuestionItem]
  );

  const videoProgressLabel = useMemo(() => {
    if (videoProgressPct >= 100) return '100% assistido';
    return `${videoProgressPct}% assistido`;
  }, [videoProgressPct]);
  const videoTimeLabel = useMemo(
    () =>
      `${formatVideoTime(videoCurrentTime)} / ${formatVideoTime(
        videoDuration
      )}`,
    [videoCurrentTime, videoDuration]
  );

  const totalVideos = watchOnlyVideoQuestions.length;
  const isLastVideo = currentVideoIndex >= totalVideos - 1;
  const hasOnlyVideoTraining =
    !!watchOnlyVideoQuestionItem && answerableTotal === 0;
  const showVideoFallbackCover =
    isNativeIosSafariVideo &&
    !videoReviewMode &&
    !isVideoPlaying &&
    videoCurrentTime <= 0;

  const clearFullscreenControlsTimer = useCallback(() => {
    if (fullscreenControlsTimerRef.current) {
      window.clearTimeout(fullscreenControlsTimerRef.current);
      fullscreenControlsTimerRef.current = null;
    }
  }, []);

  const clearFullscreenStateSyncTimers = useCallback(() => {
    fullscreenStateSyncTimersRef.current.forEach(timerId => {
      window.clearTimeout(timerId);
    });
    fullscreenStateSyncTimersRef.current = [];
  }, []);

  const revealFullscreenControls = useCallback(() => {
    clearFullscreenControlsTimer();
    setShowFullscreenControls(true);

    fullscreenControlsTimerRef.current = window.setTimeout(() => {
      setShowFullscreenControls(false);
    }, 2200);
  }, [clearFullscreenControlsTimer]);

  const syncVideoFullscreenState = useCallback(() => {
    const videoEl = videoRef.current;
    const shellEl = videoShellRef.current;
    const isFullscreenNow = isVideoInFullscreen(videoEl, shellEl);

    setIsVideoFullscreen(isFullscreenNow);
    clearFullscreenControlsTimer();
    setShowFullscreenControls(isFullscreenNow);
  }, [clearFullscreenControlsTimer]);

  const scheduleVideoFullscreenStateSync = useCallback(
    (delays = [0]) => {
      clearFullscreenStateSyncTimers();
      fullscreenStateSyncTimersRef.current = delays.map(delay =>
        window.setTimeout(() => {
          syncVideoFullscreenState();
        }, delay)
      );
    },
    [clearFullscreenStateSyncTimers, syncVideoFullscreenState]
  );

  function resetVideoGateState() {
    setVideoGateCompleted(false);
    setVideoProgressPct(0);
    setVideoReviewMode(false);
    setIsVideoPlaying(false);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setVideoLoading(true);
    setVideoLoadFailed(false);
    maxWatchedTimeRef.current = 0;
    forceSeekingRef.current = false;
  }

  const resetVideoStateForChecklist = useCallback(() => {
    setVideoGateCompleted(false);
    setVideoProgressPct(0);
    setVideoReviewMode(false);
    setCurrentVideoIndex(0);
    setIsVideoPlaying(false);
    setVideoPlaybackRate(1);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setVideoVolume(1);
    setVideoMuted(false);
    setVideoLoading(true);
    setVideoLoadFailed(false);
    maxWatchedTimeRef.current = 0;
    forceSeekingRef.current = false;
  }, []);

  const startVideoStep = useCallback(() => {
    resetVideoStateForChecklist();
    setStep('video');
  }, [resetVideoStateForChecklist, setStep]);

  const handleBackToVideoReview = useCallback(() => {
    setVideoReviewMode(true);
    setStep('video');
  }, [setStep]);

  const exitVideoReviewMode = useCallback(() => {
    setVideoReviewMode(false);
  }, []);

  function handleVideoTimeUpdate() {
    const videoEl = videoRef.current;
    if (!videoEl || forceSeekingRef.current) return;

    setVideoCurrentTime(videoEl.currentTime || 0);

    if (videoEl.currentTime > maxWatchedTimeRef.current) {
      maxWatchedTimeRef.current = videoEl.currentTime;
    }

    if (videoEl.duration > 0) {
      const nextVideoPct = Math.min(
        100,
        Math.round((maxWatchedTimeRef.current / videoEl.duration) * 100)
      );
      setVideoProgressPct(nextVideoPct);
      if (nextVideoPct >= 100) setVideoGateCompleted(true);
    }
  }

  function handleVideoSeeking() {
    const videoEl = videoRef.current;
    if (!videoEl || forceSeekingRef.current) return;
    if (videoReviewMode) return;

    if (videoEl.currentTime > maxWatchedTimeRef.current + 0.2) {
      forceSeekingRef.current = true;
      videoEl.currentTime = maxWatchedTimeRef.current;
      window.setTimeout(() => {
        forceSeekingRef.current = false;
      }, 0);
    }
  }

  function handleVideoEnded() {
    const videoEl = videoRef.current;
    if (videoEl?.duration) {
      maxWatchedTimeRef.current = videoEl.duration;
    }
    setVideoCurrentTime(videoEl?.duration || 0);
    setVideoProgressPct(100);
    setIsVideoPlaying(false);
    setVideoGateCompleted(true);
  }

  function handleVideoLoadedMetadata() {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    setVideoDuration(videoEl.duration || 0);
    setVideoCurrentTime(videoEl.currentTime || 0);
    videoEl.playbackRate = videoPlaybackRate;
    videoEl.volume = videoVolume;
    videoEl.muted = videoMuted;

    if ((videoEl.readyState || 0) >= 1) {
      setVideoLoading(false);
    }
  }

  function handleVideoCanPlay() {
    setVideoLoadFailed(false);
    setVideoLoading(false);
  }

  function handleVideoLoadStart() {
    setVideoLoadFailed(false);
    setVideoLoading(true);
  }

  function handleVideoWaiting() {
    setVideoLoading(true);
  }

  function handleVideoLoadedData() {
    setVideoLoadFailed(false);
    setVideoLoading(false);
  }

  function handleVideoSuspend() {
    const videoEl = videoRef.current;

    if ((videoEl?.readyState || 0) >= 1) {
      setVideoLoading(false);
    }
  }

  function handleVideoError() {
    setVideoLoadFailed(true);
    setVideoLoading(false);
    setIsVideoPlaying(false);
  }

  function handleVideoVolumeChange() {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const nextVolume = Number(videoEl.volume);
    setVideoVolume(Number.isFinite(nextVolume) ? nextVolume : 1);
    setVideoMuted(!!videoEl.muted || nextVolume === 0);
  }

  function handleToggleVideoPlayback() {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (videoEl.paused) {
      videoEl.play().catch(() => {});
    } else {
      videoEl.pause();
    }
  }

  function handleStartVideoFromOverlay() {
    if (
      videoLoading ||
      videoLoadFailed ||
      isVideoPlaying ||
      videoCurrentTime > 0
    )
      return;

    const videoEl = videoRef.current;
    if (!videoEl) return;

    videoEl.play().catch(() => {});
  }

  function handleVideoSurfaceClick() {
    if (videoReviewMode || videoLoading || videoLoadFailed) return;
    handleToggleVideoPlayback();
  }

  function handleVolumeChange(event) {
    const nextVolume = Math.max(
      0,
      Math.min(1, Number(event?.target?.value) || 0)
    );
    const videoEl = videoRef.current;

    setVideoVolume(nextVolume);
    setVideoMuted(nextVolume === 0);

    if (videoEl) {
      videoEl.volume = nextVolume;
      videoEl.muted = nextVolume === 0;
    }
  }

  function handleToggleMute() {
    const videoEl = videoRef.current;
    const nextMuted = !videoMuted;
    const nextVolume = !nextMuted && videoVolume === 0 ? 1 : videoVolume;

    setVideoMuted(nextMuted);

    if (!nextMuted && videoVolume === 0) {
      setVideoVolume(1);
    }

    if (videoEl) {
      if (!nextMuted && videoEl.volume === 0) {
        videoEl.volume = nextVolume;
      }
      videoEl.muted = nextMuted;
    }
  }

  function handlePlaybackRateChange(nextRate) {
    const safeRate = Number(nextRate) || 1;
    const videoEl = videoRef.current;

    setVideoPlaybackRate(safeRate);

    if (videoEl) {
      videoEl.playbackRate = safeRate;
    }
  }

  async function handleToggleFullscreen() {
    const shellEl = videoShellRef.current;
    const videoEl = videoRef.current;
    if (!shellEl && !videoEl) return;

    const isStandardFullscreen = getDocumentFullscreenElement() === shellEl;
    const isIosFullscreen = isVideoInIosNativeFullscreen(videoEl);

    if (isStandardFullscreen) {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      return;
    }

    if (isIosFullscreen) {
      if (videoEl?.webkitExitFullscreen) {
        videoEl.webkitExitFullscreen();
      }
      scheduleVideoFullscreenStateSync([0, 120, 360, 900]);
      return;
    }

    if (shellEl?.requestFullscreen) {
      await shellEl.requestFullscreen();
      return;
    }

    if (shellEl?.webkitRequestFullscreen) {
      shellEl.webkitRequestFullscreen();
      return;
    }

    if (videoEl?.webkitEnterFullscreen) {
      videoEl.webkitEnterFullscreen();
      scheduleVideoFullscreenStateSync([0, 120]);
    }
  }

  function handleContinueAfterVideo() {
    if (!videoReviewMode && !videoGateCompleted) return;

    if (!videoReviewMode) {
      setIdentificationLocked(true);
    }

    if (!videoReviewMode && !isLastVideo) {
      setCurrentVideoIndex(old => old + 1);
      resetVideoGateState();
      return;
    }

    if (!videoReviewMode && hasOnlyVideoTraining) {
      setVideoReviewMode(false);
      onOnlyVideoTrainingComplete();
      return;
    }

    setVideoReviewMode(false);
    setStep('questions');
  }

  useEffect(() => {
    if (!isNativeIosSafariVideo) return undefined;

    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) return undefined;

    const previousContent = viewportMeta.getAttribute('content') || '';
    viewportMeta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, shrink-to-fit=no, viewport-fit=cover'
    );

    return () => {
      viewportMeta.setAttribute('content', previousContent);
    };
  }, [isNativeIosSafariVideo]);

  useEffect(() => {
    function handleFullscreenChange() {
      scheduleVideoFullscreenStateSync([0, 120, 360]);
    }

    function handleVideoBeginFullscreen() {
      scheduleVideoFullscreenStateSync([0, 80]);
    }

    function handleVideoEndFullscreen() {
      scheduleVideoFullscreenStateSync([0, 120, 360, 900]);
    }

    function handlePageVisibilityOrFocus() {
      scheduleVideoFullscreenStateSync([0, 120]);
    }

    const videoEl = videoRef.current;

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handlePageVisibilityOrFocus);
    window.addEventListener('focus', handlePageVisibilityOrFocus);
    window.addEventListener('pageshow', handlePageVisibilityOrFocus);
    window.addEventListener('resize', handlePageVisibilityOrFocus);
    if (videoEl) {
      videoEl.addEventListener(
        'webkitbeginfullscreen',
        handleVideoBeginFullscreen
      );
      videoEl.addEventListener('webkitendfullscreen', handleVideoEndFullscreen);
      videoEl.addEventListener(
        'webkitpresentationmodechanged',
        handleFullscreenChange
      );
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'visibilitychange',
        handlePageVisibilityOrFocus
      );
      window.removeEventListener('focus', handlePageVisibilityOrFocus);
      window.removeEventListener('pageshow', handlePageVisibilityOrFocus);
      window.removeEventListener('resize', handlePageVisibilityOrFocus);
      if (videoEl) {
        videoEl.removeEventListener(
          'webkitbeginfullscreen',
          handleVideoBeginFullscreen
        );
        videoEl.removeEventListener(
          'webkitendfullscreen',
          handleVideoEndFullscreen
        );
        videoEl.removeEventListener(
          'webkitpresentationmodechanged',
          handleFullscreenChange
        );
      }
      clearFullscreenControlsTimer();
      clearFullscreenStateSyncTimers();
    };
  }, [
    clearFullscreenControlsTimer,
    clearFullscreenStateSyncTimers,
    scheduleVideoFullscreenStateSync,
  ]);

  useEffect(() => {
    if (step !== 'video') return undefined;

    function handleVideoKeyboardShortcuts(event) {
      const tagName = document.activeElement?.tagName?.toLowerCase();
      const isTypingTarget =
        tagName === 'input' ||
        tagName === 'textarea' ||
        document.activeElement?.isContentEditable;

      if (isTypingTarget) return;

      if (
        event.key === 'Escape' &&
        getDocumentFullscreenElement() === videoShellRef.current
      ) {
        event.preventDefault();
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        return;
      }

      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        handleToggleVideoPlayback();
      }
    }

    document.addEventListener('keydown', handleVideoKeyboardShortcuts);
    return () => {
      document.removeEventListener('keydown', handleVideoKeyboardShortcuts);
    };
  }, [step]);

  useEffect(() => {
    if (!isVideoFullscreen) {
      clearFullscreenControlsTimer();
      setShowFullscreenControls(false);
      return undefined;
    }

    revealFullscreenControls();
    return () => {
      clearFullscreenControlsTimer();
    };
  }, [
    isVideoFullscreen,
    revealFullscreenControls,
    clearFullscreenControlsTimer,
  ]);

  return {
    currentVideoIndex,
    exitVideoReviewMode,
    handleBackToVideoReview,
    handleContinueAfterVideo,
    handlePlaybackRateChange,
    handleStartVideoFromOverlay,
    handleToggleFullscreen,
    handleToggleMute,
    handleToggleVideoPlayback,
    handleVideoCanPlay,
    handleVideoEnded,
    handleVideoError,
    handleVideoLoadStart,
    handleVideoLoadedData,
    handleVideoLoadedMetadata,
    handleVideoSeeking,
    handleVideoSurfaceClick,
    handleVideoSuspend,
    handleVideoTimeUpdate,
    handleVideoVolumeChange,
    handleVideoWaiting,
    handleVolumeChange,
    hasOnlyVideoTraining,
    isLastVideo,
    isNativeIosSafariVideo,
    isVideoFullscreen,
    isVideoPlaying,
    resetVideoStateForChecklist,
    revealFullscreenControls,
    setIsVideoPlaying,
    showFullscreenControls,
    showVideoFallbackCover,
    startVideoStep,
    totalVideos,
    videoCurrentTime,
    videoGateCompleted,
    videoLoadFailed,
    videoLoading,
    videoMuted,
    videoPlaybackRate,
    videoProgressLabel,
    videoProgressPct,
    videoRef,
    videoReviewMode,
    videoShellRef,
    videoTimeLabel,
    videoVolume,
    watchOnlyVideoQuestionItem,
    watchOnlyVideoUrl,
  };
}
